/*
 * Group order for friends (docs/28): the client side of /api/group and /api/g/<token>. A member is known to the server
 * by a secret the join call hands out, kept on this phone per group (signed in, the session counts too). The host's
 * checkout is the normal bag with the group's lines copied in (store.groupCheckout).
 */
import type { LineConfig, PickupChoice } from '@tikkas/shared';
import { api } from './api.js';
import { NATIVE } from './platform.js';

export type GroupState = 'open' | 'expired' | 'locked' | 'paying' | 'paid' | 'cancelled';
export interface GroupLine { id: string; memberId: string; name: string; config: LineConfig; qty: number; linePence: number | null; title: string; description: string; mine: boolean }
export interface GroupView {
  id: string; token: string; link: string; hostName: string; state: GroupState; open: boolean; payable: boolean; expiresAt: string; readyAt: string; scheduled: boolean; pickup: PickupChoice;
  people: { id: string; name: string; isHost: boolean; mine: boolean; items: number }[];
  lines: GroupLine[];
  totalPence: number;
  limits: { people: number; lines: number; openMinutes: number };
  me: { memberId: string; name: string; isHost: boolean } | null;
  order: { id: string; number: number; status: string; readyAt: string; collectedAt: string | null } | null;
}

/* ---------- this phone's place in a group ---------- */

interface Membership { memberId: string; secret: string; name: string }
const key = (token: string) => `tikkas-group-${token.toUpperCase()}`;

export function membership(token: string): Membership | null {
  try {
    const raw = localStorage.getItem(key(token));
    return raw ? (JSON.parse(raw) as Membership) : null;
  } catch {
    return null;
  }
}
function remember(token: string, m: Membership): void {
  try {
    localStorage.setItem(key(token), JSON.stringify(m));
  } catch {
    /* private mode: the session still works when signed in */
  }
}
/** The header that proves this phone's place (empty when it has none; a signed-in member needs none). */
export function memberHeaders(token: string): Record<string, string> {
  const m = membership(token);
  return m && m.secret ? { 'x-tikkas-group-member': `${m.memberId}:${m.secret}` } : {};
}

/* ---------- calls ---------- */

export const groupApi = {
  start: (pickup: PickupChoice) => api<{ group: GroupView }>('/api/group', { body: { pickup } }).then((r) => r.group),
  mine: () => api<{ groups: GroupView[] }>('/api/group/mine').then((r) => r.groups),
  get: (token: string) => api<{ group: GroupView }>(`/api/g/${encodeURIComponent(token)}`, { headers: memberHeaders(token) }).then((r) => r.group),
  async join(token: string, name: string): Promise<GroupView> {
    const r = await api<{ memberId: string; secret: string; name: string; group: GroupView }>(`/api/g/${encodeURIComponent(token)}/join`, { body: { name } });
    if (r.secret) remember(token, { memberId: r.memberId, secret: r.secret, name: r.name });
    return r.group;
  },
  addLine: (token: string, config: LineConfig, qty: number) => api<{ group: GroupView }>(`/api/g/${encodeURIComponent(token)}/lines`, { body: { config, qty }, headers: memberHeaders(token) }).then((r) => r.group),
  updateLine: (token: string, id: string, config: LineConfig, qty: number) => api<{ group: GroupView }>(`/api/g/${encodeURIComponent(token)}/lines/${encodeURIComponent(id)}`, { method: 'PUT', body: { config, qty }, headers: memberHeaders(token) }).then((r) => r.group),
  removeLine: (token: string, id: string) => api<{ group: GroupView }>(`/api/g/${encodeURIComponent(token)}/lines/${encodeURIComponent(id)}`, { method: 'DELETE', headers: memberHeaders(token) }).then((r) => r.group),
  act: (token: string, action: 'lock' | 'unlock' | 'cancel') => api<{ group: GroupView }>(`/api/g/${encodeURIComponent(token)}/${action}`, { body: {}, headers: memberHeaders(token) }).then((r) => r.group),
};

/* ---------- sharing ---------- */

/** The system share sheet: Capacitor's on the phone, the browser's on the web, else the link goes on the clipboard. */
export async function shareLink(a: { url: string; title: string; text: string }): Promise<'shared' | 'copied' | 'failed'> {
  if (NATIVE) {
    try {
      const { Share } = await import('@capacitor/share');
      await Share.share({ title: a.title, text: a.text, url: a.url, dialogTitle: a.title });
      return 'shared';
    } catch {
      /* the plugin isn't in this build yet, or the sheet was dismissed: try the web way */
    }
  }
  if (typeof navigator.share === 'function') {
    try {
      await navigator.share({ title: a.title, text: a.text, url: a.url });
      return 'shared';
    } catch {
      return 'failed';
    }
  }
  try {
    await navigator.clipboard.writeText(a.url);
    return 'copied';
  } catch {
    return 'failed';
  }
}

/** "Collect at 12:45" or "Ready about 12:45" for the group's heading, with the day when it isn't today. */
export function collectLine(g: Pick<GroupView, 'readyAt' | 'scheduled'>, readyWhen: (iso: string) => string): string {
  const w = readyWhen(g.readyAt);
  return g.scheduled ? `Collect ${w.replace(/^about /, 'at ')}` : `Ready ${w}`;
}
